import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useConfig } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Settings, Loader2 } from "lucide-react";
import BillingSection from "@/components/BillingSection";

function PhoneVerify({ onDone }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
  };
  const send = () => run(async () => { await api.post("/account/phone/start", { phone }); setSent(true); toast.success("Code sent"); });
  const verify = () => run(async () => { await api.post("/account/phone/verify", { phone, code }); toast.success("Phone verified"); onDone(); });

  return (
    <div className="space-y-3">
      <Label className="font-mono-label">Mobile number</Label>
      <div className="flex gap-2">
        <Input data-testid="settings-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+14155552671" className="rounded-sm" disabled={sent} />
        {!sent && <Button data-testid="settings-phone-send" onClick={send} disabled={busy || !phone} className="rounded-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"}</Button>}
      </div>
      {sent && (
        <div className="flex gap-2">
          <Input data-testid="settings-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="rounded-sm" />
          <Button data-testid="settings-code-verify" onClick={verify} disabled={busy || !code} className="rounded-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}</Button>
        </div>
      )}
    </div>
  );
}

function BioField({ user, refresh }) {
  const [bio, setBio] = useState(user?.bio || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch("/account/settings", { bio }); await refresh(); toast.success("Tagline saved"); }
    catch { toast.error("Couldn't save"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Label htmlFor="bio" className="font-mono-label">Tagline on shared videos</Label>
      <div className="flex gap-2">
        <Input id="bio" data-testid="settings-bio-input" value={bio} maxLength={100} onChange={(e) => setBio(e.target.value)}
          placeholder="e.g. Contract lawyer in Austin" className="rounded-sm" />
        <Button data-testid="settings-bio-save" onClick={save} disabled={busy || bio === (user?.bio || "")} className="rounded-sm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Shown under your name when someone opens a video you shared. {bio.length}/100</p>
    </div>
  );
}

export default function AccountSettings() {
  const { user, refresh } = useAuth();
  const cfg = useConfig();

  const toggle = async (v) => {
    try { await api.patch("/account/settings", { sms_alerts: v }); refresh(); } catch { toast.error("Couldn't save"); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="dashboard-settings"><Settings className="w-4 h-4 mr-1.5" /> Settings</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">Profile</DialogTitle></DialogHeader>
        <BioField user={user} refresh={refresh} />
        <div className="flex items-center justify-between gap-4 border border-border p-4">
          <Label htmlFor="milestone-emails" className="text-sm">Email me when a shared video reaches 10, 100 or 1,000 views</Label>
          <Switch id="milestone-emails" data-testid="settings-milestone-toggle" checked={user?.milestone_emails !== false}
            onCheckedChange={async (v) => {
              try { await api.patch("/account/settings", { milestone_emails: v }); refresh(); } catch { toast.error("Couldn't save"); }
            }} />
        </div>
        <div className="flex items-center justify-between gap-4 border border-border p-4">
          <div>
            <Label htmlFor="login-alerts" className="text-sm">Email me every time my account is signed in to</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Includes the device, approximate location and a one-click reset link.</p>
          </div>
          <Switch id="login-alerts" data-testid="settings-login-alerts-toggle" checked={user?.login_alerts !== false}
            onCheckedChange={async (v) => {
              try { await api.patch("/account/settings", { login_alerts: v }); refresh(); } catch { toast.error("Couldn't save"); }
            }} />
        </div>
        <div className="flex items-center justify-between gap-4 border border-border p-4">
          <div>
            <Label htmlFor="bot-digest" className="text-sm">Weekly chatbot report every Monday</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Questions, visitors and the most-asked questions per chatbot — only sent for weeks with activity.</p>
          </div>
          <Switch id="bot-digest" data-testid="settings-bot-digest-toggle" checked={user?.bot_digest !== false}
            onCheckedChange={async (v) => {
              try { await api.patch("/account/settings", { bot_digest: v }); refresh(); } catch { toast.error("Couldn't save"); }
            }} />
        </div>
        <div className="border-t border-border pt-5 font-serif-display text-2xl">Billing</div>
        <BillingSection refresh={refresh} />
        <div className="border-t border-border pt-5 font-serif-display text-2xl">Text messages</div>
        {!cfg?.sms_enabled ? (
          <p data-testid="settings-sms-disabled" className="text-sm text-muted-foreground">Text messages aren't set up yet.</p>
        ) : user?.phone_verified ? (
          <div className="space-y-5">
            <p className="text-sm">Verified number: <strong data-testid="settings-phone-verified">{user.phone}</strong>. You can also sign in with a code sent to this number.</p>
            <div className="flex items-center justify-between border border-border p-4">
              <Label htmlFor="sms-alerts" className="text-sm">Text me when a document is ready</Label>
              <Switch id="sms-alerts" data-testid="settings-sms-toggle" checked={!!user.sms_alerts} onCheckedChange={toggle} />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Add your mobile number to get a text when a document is ready, and to sign in with a one-time code.</p>
            <PhoneVerify onDone={refresh} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
